import { auth } from '../firebase';
import {
  addNationalizationLifecycleEventToUpdates,
  enqueueNationalizationLifecycleEvent,
  flushNationalizationLifecycleOutbox,
  removeNationalizationLifecycleEvent
} from './nationalizationLifecycleOutbox';

// Keep every nationalization request behind one authenticated client. This makes
// it much harder for a component to accidentally call a compliance endpoint
// without the signed-in employer's Firebase identity token.
const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL_PROD
  || process.env.REACT_APP_BACKEND_URL
  || 'http://localhost:8000'
).replace(/\/$/, '');

export const NATIONALIZATION_WORKFORCE_CHANGED_EVENT = 'nationalization:workforce-changed';

async function getHeaders({ json = true } = {}) {
  const user = auth.currentUser;
  const headers = json ? { 'Content-Type': 'application/json' } : {};
  if (!user) return headers;

  const token = await user.getIdToken();
  return { ...headers, Authorization: `Bearer ${token}` };
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function request(path, { method = 'GET', body, signal, formData = false } = {}) {
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method,
    signal,
    headers: await getHeaders({ json: !formData }),
    body: body === undefined
      ? undefined
      : formData
        ? body
        : JSON.stringify(body)
  });
  const payload = await parseResponse(response);

  if (!response.ok || payload?.success === false) {
    const error = new Error(payload?.detail || payload?.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

async function requestWithTransientRetry(path, options = {}, attempts = 3) {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await request(path, options);
    } catch (error) {
      lastError = error;
      const transient = !error?.status || error.status === 429 || error.status >= 500;
      if (!transient || options.signal?.aborted || attempt === attempts - 1) throw error;
      // Lifecycle writes are idempotent: the backend rereads the same employee
      // record, so a short retry safely covers a temporary network/backend drop.
      await wait(attempt === 0 ? 250 : 750);
    }
  }
  throw lastError;
}

function queryString(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  const value = query.toString();
  return value ? `?${value}` : '';
}

export const nationalizationService = {
  backendUrl: BACKEND_URL,

  getCountries({ signal } = {}) {
    return request('/nationalization/countries', { signal });
  },

  getProfile(companyUID, { signal } = {}) {
    return request(`/nationalization/profile${queryString({ company_uid: companyUID })}`, { signal });
  },

  getOperationalContext(companyUID, { signal } = {}) {
    return request(`/nationalization/operational-context${queryString({ company_uid: companyUID })}`, { signal });
  },

  saveProfile(companyUID, profile, { signal } = {}) {
    return request('/nationalization/profile', {
      method: 'PUT',
      body: { company_uid: companyUID, profile },
      signal
    });
  },

  getOverview(companyUID, {
    asOf,
    forecastDays = 90,
    establishmentId,
    signal
  } = {}) {
    return request(`/nationalization/overview${queryString({
      company_uid: companyUID,
      as_of: asOf,
      forecast_days: forecastDays,
      establishment_id: establishmentId
    })}`, { signal });
  },

  recalculate(companyUID, { asOf, forecastDays = 90, establishmentId, signal } = {}) {
    return request('/nationalization/recalculate', {
      method: 'POST',
      body: {
        company_uid: companyUID,
        as_of: asOf,
        forecast_days: forecastDays,
        establishment_id: establishmentId
      },
      signal
    });
  },

  suggestJobCodes(companyUID, employee, { limit = 5, signal } = {}) {
    return request('/nationalization/job-codes/suggest', {
      method: 'POST',
      body: {
        company_uid: companyUID,
        employee_id: employee?.employeeId || employee?.id,
        country_code: employee?.countryCode || employee?.country_code,
        title: employee?.title || employee?.role || '',
        duties: employee?.duties || '',
        qualifications: employee?.qualifications || '',
        limit
      },
      signal
    });
  },

  confirmJobCode(companyUID, employeeId, mapping, { signal } = {}) {
    return request('/nationalization/job-codes/confirm', {
      method: 'POST',
      body: {
        company_uid: companyUID,
        employee_id: employeeId,
        country_code: mapping?.countryCode || mapping?.country_code,
        occupation_code: mapping?.code || mapping?.occupation_code,
        occupation_group: mapping?.occupationGroup || mapping?.occupation_group,
        is_group: mapping?.isGroup ?? mapping?.is_group,
        occupation_label: mapping?.label || mapping?.occupation_label,
        occupation_label_ar: mapping?.labelAr || mapping?.label_ar || mapping?.occupation_label_ar,
        suggestion_run_id: mapping?.suggestionRunId || mapping?.suggestion_run_id
      },
      signal
    });
  },

  saveEmployeeFacts(companyUID, employees, {
    importId,
    factsBindingToken,
    importScope,
    fullWorkforceAttested,
    expectedEmployeeIds,
    expectedChunkCount,
    chunkIndex,
    finalChunk,
    signal
  } = {}) {
    const importBinding = importId ? {
      import_id: importId,
      facts_binding_token: factsBindingToken,
      import_scope: importScope,
      full_workforce_attested: fullWorkforceAttested === true,
      expected_employee_ids: Array.isArray(expectedEmployeeIds) ? expectedEmployeeIds : [],
      expected_chunk_count: Number(expectedChunkCount),
      chunk_index: Number(chunkIndex),
      final_chunk: finalChunk === true
    } : {};
    // The backend chunk endpoint is idempotent for this import/token/index/body.
    // Retry transient response loss with the same in-memory binding; a user-clicked
    // re-import still creates an entirely new session in My Team.
    return requestWithTransientRetry('/nationalization/employees/facts/bulk', {
      method: 'POST',
      body: {
        company_uid: companyUID,
        ...importBinding,
        employees: Array.isArray(employees) ? employees : []
      },
      signal
    });
  },

  async syncEmployeeLifecycle(companyUID, employeeId, {
    eventId,
    source = 'employee_lifecycle',
    changeKinds = ['lifecycle'],
    establishmentId,
    signal
  } = {}) {
    // Legacy callers still become durable: if they did not atomically attach an
    // outbox entry to their employee write, create one before contacting the API.
    const queued = eventId
      ? { eventId }
      : await enqueueNationalizationLifecycleEvent({
        companyUID,
        employeeId,
        source,
        changeKinds,
        establishmentId
      });
    try {
      const result = await requestWithTransientRetry(
        `/nationalization/employees/${encodeURIComponent(employeeId)}/sync-lifecycle`,
        {
          method: 'POST',
          body: {
            company_uid: companyUID,
            idempotency_key: queued.eventId,
            source,
            change_kinds: changeKinds,
            establishment_id: establishmentId || undefined
          },
          signal
        }
      );
      await removeNationalizationLifecycleEvent(companyUID, queued.eventId);
      return result;
    } catch (error) {
      // Never delete on failure. The durable marker is replayed when this page is
      // online/visible again, including after a browser restart.
      throw error;
    }
  },

  flushLifecycleOutbox(companyUID, { signal } = {}) {
    return flushNationalizationLifecycleOutbox(companyUID, (event) => (
      requestWithTransientRetry(
        `/nationalization/employees/${encodeURIComponent(event.employeeId)}/sync-lifecycle`,
        {
          method: 'POST',
          body: {
            company_uid: companyUID,
            idempotency_key: event.eventId,
            source: event.source,
            change_kinds: event.changeKinds,
            establishment_id: event.establishmentId
          },
          signal
        }
      )
    ));
  },

  saveReconciliation(companyUID, reconciliation, { signal } = {}) {
    return request('/nationalization/reconciliations', {
      method: 'POST',
      body: { company_uid: companyUID, ...reconciliation },
      signal
    });
  },

  acknowledgeRule(companyUID, ruleId, ruleVersion, { signal } = {}) {
    return request('/nationalization/rules/acknowledge', {
      method: 'POST',
      body: {
        company_uid: companyUID,
        rule_id: ruleId,
        rule_version: ruleVersion
      },
      signal
    });
  }
};

// Mount this from any authenticated workspace that knows the resolved company ID.
// It replays immediately, when connectivity returns, when a hidden tab becomes
// visible, and periodically while the page stays open.
export function startNationalizationLifecycleReplay(companyUID) {
  if (!companyUID || typeof window === 'undefined') return () => {};
  let stopped = false;
  const replay = () => {
    if (stopped) return;
    nationalizationService.flushLifecycleOutbox(companyUID)
      .then((result) => {
        if (!stopped && Number(result?.delivered || 0) > 0) {
          // An overview GET may have finished before the background drain. Emit
          // only after delivery/recalculation commits so an open screen reloads
          // the new persisted snapshot instead of remaining silently stale.
          announceNationalizationWorkforceChange({
            companyUID,
            source: 'lifecycle_outbox_replay',
            recalculated: true,
            delivered: result.delivered
          });
        }
      })
      .catch((error) => {
        console.warn('Nationalization lifecycle outbox replay is still pending:', error);
      });
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') replay();
  };
  replay();
  window.addEventListener('online', replay);
  document.addEventListener('visibilitychange', onVisibility);
  const timer = window.setInterval(replay, 45000);
  return () => {
    stopped = true;
    window.removeEventListener('online', replay);
    document.removeEventListener('visibilitychange', onVisibility);
    window.clearInterval(timer);
  };
}

export { addNationalizationLifecycleEventToUpdates };

// Imports and JOBSEEQR hires use this lightweight browser event to refresh an
// already-open overview. The backend remains the source of truth and performs
// the actual full-workforce calculation.
export function announceNationalizationWorkforceChange(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(NATIONALIZATION_WORKFORCE_CHANGED_EVENT, { detail }));
}

export default nationalizationService;
